import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import Layout from '../Layout';
import {
  FixedContainer,
  Title,
  ContentContainer,
  MediaContainer,
  SelectButtonsContainer,
  TitleContainer,
  SubTitle,
  Row,
} from './styled';
import { AddButton } from '../UI/Button';
import { ActionLabel } from '../UI/ActionLabel';
import Uploader from '../Uploader';
import Media from './Media';
import MediaPreviewEdit from './Preview/Edit';
import { Loading } from '../UI/Loaders';
import strings from '../../constants/strings';
import { tabs, getFilteredMedia } from './common';
import Tabs from '../UI/Tabs';
import MediaLibraryContent from './Content';
import {
  useDeleteAssets,
  useGetAssets,
  useUploadAsset,
} from '../../contexts/api/asset';
import { useSnackbar, useModal } from '../../contexts/ui';
import { IconButton } from '@material-ui/core';

const normalizeMediaFilterValue = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const MEDIA_FILTER_STORAGE_KEY = 'mediaLibraryFilterQuery';

const MediaLibrary = () => {
  const {
    assets,
    loading,
    error: errorGettingAssets,
    getAssets,
  } = useGetAssets();

  const {
    uploadAsset,
    loading: uploadInProgress,
    processing: processingUpload,
    error: errorUploadingAsset,
  } = useUploadAsset();

  const {
    deleteAssets,
    error: errorDeletingAssets,
    loading: deleteAssetsInProgress,
  } = useDeleteAssets();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selecting, setSelecting] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState({});
  const [filterQuery, setFilterQuery] = useState(
    () => sessionStorage.getItem(MEDIA_FILTER_STORAGE_KEY) || ''
  );

  const numberOfSelectedAssets = Object.keys(selectedAssets).length;

  const getSelectedTabType = () => tabs[selectedTabIndex]?.type;

  const filteredByTabMedia = getFilteredMedia(assets, getSelectedTabType());
  const normalizedFilterQuery = normalizeMediaFilterValue(filterQuery.trim());
  const visibleMedia = normalizedFilterQuery
    ? filteredByTabMedia.filter((asset) =>
        normalizeMediaFilterValue(asset.name).includes(normalizedFilterQuery)
      )
    : filteredByTabMedia;

  const { showSnackbar } = useSnackbar();
  const { openModal } = useModal();

  const error =
    errorGettingAssets || errorDeletingAssets || errorUploadingAsset;
  const errorMessage = error?.message || '';

  useEffect(() => {
    if (Boolean(errorMessage)) {
      showSnackbar(errorMessage, 'error');
    }
  }, [errorMessage, showSnackbar]);

  useEffect(() => {
    sessionStorage.setItem(MEDIA_FILTER_STORAGE_KEY, filterQuery);
  }, [filterQuery]);

  const cancelSelecting = () => {
    setSelectedAssets({});
    setSelecting(false);
  };

  const selectAll = () => {
    const selected = {};

    getFilteredMedia(assets, getSelectedTabType()).forEach((m) => {
      selected[m.id] = m;
    });

    setSelectedAssets(selected);
  };

  const handleDeleteConfirm = async () => {
    const result = await deleteAssets(Object.values(selectedAssets));
    await getAssets();

    if (result.success) {
      showSnackbar(strings.en.mediaLibrary.deleteAssets.successMessage);
      cancelSelecting();
    } else {
      showSnackbar(strings.en.mediaLibrary.deleteAssets.errorMessage, 'error');
    }
  };

  const handleDelete = () => {
    if (numberOfSelectedAssets) {
      openModal({
        title: 'Delete',
        content: strings.en.mediaLibrary.deleteAssets.confirmationMessage(
          numberOfSelectedAssets,
        ),
        action: handleDeleteConfirm,
        actionLabel: 'Delete',
        actionColor: 'red',
      });
    }
  };

  const handlePreviewMedia = (asset) => {
    openModal({
      type: 'modal',
      size: 'lg',
      action: null,
      cancelLabel: 'Close',
      title: <MediaPreviewEdit media={asset} />,
      content: <Media preview media={asset} />,
    });
  };

  const handleMediaClick = (m) => {
    if (selecting) {
      if (selectedAssets[m.id]) {
        setSelectedAssets(_.omit(selectedAssets, m.id));
      } else {
        setSelectedAssets({ ...selectedAssets, [m.id]: m });
      }
    } else {
      handlePreviewMedia(m);
    }
  };

  const uploadMedia = async (media) => {
    const files = Array.isArray(media) ? media : [media];

    const results = await Promise.all(files.map((file) => uploadAsset(file)));
    await getAssets();

    const allSucceeded = results.every((result) => result.success);

    if (allSucceeded) {
      showSnackbar(strings.en.mediaLibrary.uploadAsset.successMessage);
    } else {
      showSnackbar(strings.en.mediaLibrary.uploadAsset.errorMessage, 'error');
    }
  };

  const actionInProgress = uploadInProgress || deleteAssetsInProgress;

  const action = getActionMessage({
    processingUpload,
    uploadInProgress,
    deleteAssetsInProgress,
    strings,
  });

  return (
    <Layout noBodyPadding bgColor="var(--color-offwhite)">
      <ContentContainer>
        <FixedContainer>
          <Row>
            <TitleContainer>
              <Title>{`Media Library`}</Title>
              <SubTitle>
                {action}
                {actionInProgress && <Loading />}
              </SubTitle>
            </TitleContainer>
            <Uploader
              id="package-card"
              accept="video/*,audio/*,image/*,application/pdf,application/json"
              onChange={uploadMedia}
              multiple
            >
              <AddButton
                as="div"
                size="s"
                height="inherit"
                disabled={actionInProgress}
              >
                {'Upload New Media'}
              </AddButton>
            </Uploader>
          </Row>
          <Row>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Tabs
                tabs={tabs.map((tab) => tab.label)}
                onSelectTab={setSelectedTabIndex}
                selectedTabIndex={selectedTabIndex}
              />
              <div
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  width: '260px',
                }}
              >
                <input
                  type="text"
                  placeholder="Filter"
                  value={filterQuery}
                  onChange={(event) => setFilterQuery(event.target.value)}
                  style={{
                    width: '100%',
                    paddingRight: '32px',
                    boxSizing: 'border-box',
                  }}
                />
                {filterQuery ? (
                  <IconButton
                    size="small"
                    onClick={() => setFilterQuery('')}
                    style={{
                      position: 'absolute',
                      right: '4px',
                      padding: '4px',
                    }}
                    aria-label="Clear filter"
                  >
                    x
                  </IconButton>
                ) : null}
              </div>
            </div>
            {selecting ? (
              <SelectButtonsContainer>
                <ActionLabel delete onClick={handleDelete}>
                  Delete
                </ActionLabel>
                <ActionLabel onClick={selectAll}>Select All</ActionLabel>
                <ActionLabel onClick={cancelSelecting}>Cancel</ActionLabel>
              </SelectButtonsContainer>
            ) : (
              <ActionLabel onClick={() => setSelecting(true)}>
                Select Files
              </ActionLabel>
            )}
          </Row>
        </FixedContainer>
        <MediaContainer>
          {loading ? (
            <Loading />
          ) : (
            <MediaLibraryContent
              media={visibleMedia}
              onClickMedia={handleMediaClick}
              isSelected={(m) => selectedAssets[m.id]}
            />
          )}
        </MediaContainer>
      </ContentContainer>
    </Layout>
  );
};

function getActionMessage({
  processingUpload,
  uploadInProgress,
  deleteAssetsInProgress,
  strings,
}) {
  return processingUpload
    ? strings.en.mediaLibrary.uploadAsset.processing
    : uploadInProgress
    ? strings.en.mediaLibrary.uploadAsset.inProgress
    : deleteAssetsInProgress
    ? strings.en.mediaLibrary.deleteAssets.inProgress
    : '';
}

export default MediaLibrary;
